/**
 * The lesson page's blocks (docs/academy-curriculum.md, "How a lesson is delivered"): one component per block type in
 * LessonBlockSchema, each a card with a muted uppercase label, in the look of docs/academy-sample-lesson.html. A block
 * whose type the page does not know renders nothing and warns, so a newer lesson never breaks an older page.
 */
import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { LessonBlock } from '@diggens/types';
import { DIAGRAMS, DIAGRAM_TITLES } from './diagrams';

type BlockOf<T extends LessonBlock['type']> = Extract<LessonBlock, { type: T }>;

/** The theme's overline, as the sample's muted uppercase labels: a little larger for a section, tighter on one line. */
const labelSx = { fontSize: 13, letterSpacing: '0.06em', lineHeight: 1.4, display: 'block' } as const;

/** One card section: the muted uppercase label, then its content. */
export function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Paper component="section" variant="outlined" sx={{ borderRadius: 1.5, px: { xs: 2, sm: 2.75 }, py: 2.5 }}>
      <Typography component="h2" variant="overline" color="text.secondary" sx={{ ...labelSx, mb: 1.5 }}>
        {label}
      </Typography>
      {children}
    </Paper>
  );
}

export function DiagramBlock({ block }: { block: BlockOf<'diagram'> }) {
  const Diagram: React.FC | undefined = DIAGRAMS[block.diagram];
  // A page loaded before a diagram name was added to the drawn set has no component for it: skip it, never throw
  if (!Diagram) {
    console.warn('Academy diagram not drawn in this build, skipped', block.diagram);
    return null;
  }
  return (
    <Section label="The picture">
      {/* On a phone the picture keeps a readable width and scrolls sideways instead of shrinking its labels */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box data-testid={`diagram-${block.diagram}`} aria-label={DIAGRAM_TITLES[block.diagram]} sx={{ minWidth: { xs: 560, sm: 0 } }}>
          <Diagram />
        </Box>
      </Box>
      {block.caption && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
          {block.caption}
        </Typography>
      )}
    </Section>
  );
}

export function PointsBlock({ block }: { block: BlockOf<'points'> }) {
  return (
    <Section label={block.heading}>
      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { my: 0.75 } }}>
        {block.items.map((item, index) => (
          <Typography component="li" key={index} variant="body1">
            {item}
          </Typography>
        ))}
      </Box>
    </Section>
  );
}

/** `highlight` lists the rows the reader should look at, by 0-based index, as the generator writes them. */
export function ExampleBlock({ block }: { block: BlockOf<'example'> }) {
  const highlighted = new Set(block.highlight);
  return (
    <Section label={block.heading}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& th, & td': { px: 1.25, py: 1, verticalAlign: 'top' } }}>
          <TableHead>
            <TableRow>
              {block.columns.map((column) => (
                <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="overline" color="text.secondary" sx={{ ...labelSx, fontSize: 12, letterSpacing: '0.05em' }}>
                    {column}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {block.rows.map((row, rowIndex) => {
              const win = highlighted.has(rowIndex);
              return (
                <TableRow key={rowIndex} data-highlighted={win ? 'true' : undefined} sx={win ? { '& td': { bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12), fontWeight: 600 } } : undefined}>
                  {block.columns.map((_, columnIndex) => (
                    <TableCell key={columnIndex}>{row[columnIndex] ?? ''}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
}

export function CompareBlock({ block }: { block: BlockOf<'compare'> }) {
  const column = { border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.75, py: 1.5, flex: 1 } as const;
  return (
    <Section label="People assume · What is true">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75}>
        <Box sx={column}>
          <Typography component="b" variant="overline" color="text.secondary" sx={{ ...labelSx, fontSize: 12, letterSpacing: '0.05em', mb: 0.75 }}>
            People assume
          </Typography>
          <Typography variant="body2">{block.assumed}</Typography>
        </Box>
        <Box sx={column}>
          <Typography component="b" variant="overline" color="text.secondary" sx={{ ...labelSx, fontSize: 12, letterSpacing: '0.05em', mb: 0.75 }}>
            What is true
          </Typography>
          <Typography variant="body2">{block.actual}</Typography>
        </Box>
      </Stack>
    </Section>
  );
}

export function TryItBlock({ block }: { block: BlockOf<'try_it'> }) {
  const external = /^https?:\/\//.test(block.href);
  return (
    <Section label="Try it">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Typography variant="body1">{block.text}</Typography>
        <Button variant="contained" href={block.href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {block.label}
        </Button>
      </Stack>
    </Section>
  );
}

/** Renders one block by its type. An unknown type is skipped with a warning, never thrown. */
export function LessonBlockView({ block }: { block: LessonBlock }) {
  switch (block.type) {
    case 'diagram':
      return <DiagramBlock block={block} />;
    case 'points':
      return <PointsBlock block={block} />;
    case 'example':
      return <ExampleBlock block={block} />;
    case 'compare':
      return <CompareBlock block={block} />;
    case 'try_it':
      return <TryItBlock block={block} />;
    default: {
      const unknown: { type?: unknown } = block;
      console.warn('Academy lesson block of unknown type skipped', unknown.type);
      return null;
    }
  }
}
